import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useAppContext, useAsyncData } from '@/hooks';
import { institutionFormSchemasApi } from '@/api/config-governance';
import {
  FORM_DOMAINS,
  LIFECYCLE_STATUSES,
  type FormDomain,
  type FormSchema,
} from '@/types/config-governance';
import styles from '../platform/platform-pages.module.css';
import gov from '../platform/governance.module.css';

/**
 * CFG-11 — the admission form register for THIS institution.
 *
 * Where the platform console's register (FormSchemasPage) mixes platform
 * templates and every institution's derivations behind a scope filter, this
 * screen is scoped by construction: the institution comes from the active
 * admin context, and every schema listed is this school's own. The one scope
 * choice left — deriving from a platform template — is offered by the create
 * dialog, which lists the ACTIVE platform templates via
 * includePlatformTemplates=true so the institution can pick a base whose
 * protected fields then become the floor validation holds it to.
 */

export function AdminFormSchemasPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const status = params.get('status') ?? '';
  const [creating, setCreating] = useState(false);

  const fetcher = useCallback(
    () =>
      institutionFormSchemasApi.list(institutionId, {
        ...(domain ? { formDomain: domain as FormDomain } : {}),
        ...(status ? { status } : {}),
      }),
    [institutionId, domain, status],
  );

  const { state, reload } = useAsyncData<FormSchema[]>(
    `institution-form-schemas:${institutionId}:${domain}:${status}`,
    fetcher,
    !!institutionId,
  );

  const setFilters = (nextDomain: string, nextStatus: string) => {
    const next = new URLSearchParams();
    if (nextDomain) next.set('domain', nextDomain);
    if (nextStatus) next.set('status', nextStatus);
    setParams(next);
  };

  const groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    const byDomain = new Map<string, FormSchema[]>();
    for (const schema of state.data) {
      const list = byDomain.get(schema.formDomain) ?? [];
      list.push(schema);
      byDomain.set(schema.formDomain, list);
    }
    return FORM_DOMAINS.filter((d) => byDomain.has(d)).map(
      (d) => [d, byDomain.get(d) as FormSchema[]] as const,
    );
  }, [state]);

  if (!institutionId) return null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('formSchemas.title')}</h1>
          <p className={styles.pageLead}>{t('admin.formSchemas.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          {t('formSchemas.newSchema')}
        </button>
      </header>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="admin-schema-domain">
            {t('formSchemas.filterDomain')}
          </label>
          <Select
            id="admin-schema-domain"
            value={domain}
            onChange={(next) => setFilters(next, status)}
            options={[
              { value: '', label: t('formSchemas.filterAllDomains') },
              ...FORM_DOMAINS.map((value) => ({
                value,
                label: t(`formSchemas.domains.${value}`, { defaultValue: value }),
              })),
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="admin-schema-status">
            {t('acadPolicies.filterStatus')}
          </label>
          <Select
            id="admin-schema-status"
            value={status}
            onChange={(next) => setFilters(domain, next)}
            options={[
              { value: '', label: t('grading.filterAllStatuses') },
              ...LIFECYCLE_STATUSES.map((value) => ({
                value,
                label: t(`platform.state.lifecycle.${value}`),
              })),
            ]}
          />
        </div>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('formSchemas.emptyTitle')}
        emptyBody={t('admin.formSchemas.emptyBody')}
      >
        {() => (
          <>
            {groups.map(([groupDomain, schemas]) => (
              <section key={groupDomain}>
                <h2 className={styles.subheading}>
                  {t(`formSchemas.domains.${groupDomain}`, { defaultValue: groupDomain })}
                </h2>
                <ul className={styles.rowList}>
                  {schemas.map((schema) => (
                    <li key={schema.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <Link
                          className={styles.rowTitleLink}
                          to={`/admin/institution/form-schemas/${encodeURIComponent(schema.id)}`}
                        >
                          {schema.title}
                        </Link>
                        <div className={styles.rowMetaLine}>
                          <code className={styles.codeTag}>{schema.code}</code>
                          <span className={styles.mutedTag}>
                            {t('grading.versionShort', { n: schema.version })}
                          </span>
                          <StateBadge kind="lifecycle" value={schema.status} size="sm" />
                        </div>
                        {schema.description && (
                          <p className={styles.rowDetail}>{schema.description}</p>
                        )}
                      </div>
                      <div className={styles.rowAside}>
                        <span className={styles.mutedCell}>
                          {t('formSchemas.contentSummary', {
                            sections: schema.sections.length,
                            fields: schema.fields.length,
                          })}
                        </span>
                        <span className={styles.mutedCell}>
                          {t('formSchemas.protectedCount', {
                            total: schema.fields.filter((field) => field.protected).length,
                          })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </AsyncPanel>

      <CreateSchemaDialog
        institutionId={institutionId}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          toast.success(t('formSchemas.createdToast', { code: created.code, n: created.version }));
          navigate(`/admin/institution/form-schemas/${encodeURIComponent(created.id)}`);
        }}
      />
    </div>
  );
}

function CreateSchemaDialog({
  institutionId,
  open,
  onClose,
  onCreated,
}: {
  institutionId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (created: FormSchema) => void;
}) {
  const { t } = useTranslation();
  const [formDomain, setFormDomain] = useState<FormDomain>('ADMISSION_APPLICATION');
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [baseSchemaId, setBaseSchemaId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform templates of the SAME domain, offered as derivation bases. The
  // list call appends the ACTIVE platform templates to this institution's own
  // rows; the base picker keeps only the platform ones.
  const baseFetcher = useCallback(
    () =>
      institutionFormSchemasApi.list(institutionId, {
        formDomain,
        includePlatformTemplates: true,
      }),
    [institutionId, formDomain],
  );
  const bases = useAsyncData<FormSchema[]>(
    `institution-form-schema-bases:${institutionId}:${formDomain}:${open ? 'open' : 'closed'}`,
    baseFetcher,
    open,
  );

  const platformTemplates =
    bases.state.status === 'ready'
      ? bases.state.data.filter((candidate) => candidate.institutionId === null)
      : [];

  const codeValid = /^[A-Z][A-Z0-9_]*$/.test(code);
  const canSubmit =
    codeValid &&
    title.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Sections and fields are copied from the base when deriving, so the
      // protected floor arrives intact instead of instantly violated. The
      // institution comes from the route — the body never carries it.
      const base =
        baseSchemaId && bases.state.status === 'ready'
          ? bases.state.data.find((candidate) => candidate.id === baseSchemaId)
          : undefined;
      const created = await institutionFormSchemasApi.create(institutionId, {
        formDomain,
        code,
        title: title.trim(),
        effectiveFrom,
        sections: base ? base.sections : [],
        fields: base ? base.fields : [],
        ...(baseSchemaId ? { baseSchemaId } : {}),
      });
      onCreated(created);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('formSchemas.createTitle')}
      description={t('formSchemas.createIntro')}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="admin-new-schema-domain">
            {t('formSchemas.fieldDomain')}
          </label>
          <Select
            id="admin-new-schema-domain"
            value={formDomain}
            onChange={(next) => {
              setFormDomain(next as FormDomain);
              setBaseSchemaId('');
            }}
            options={FORM_DOMAINS.map((value) => ({
              value,
              label: t(`formSchemas.domains.${value}`, { defaultValue: value }),
            }))}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="admin-new-schema-code">
            {t('formSchemas.fieldCode')}
          </label>
          <p className={styles.fieldHint}>{t('grading.fieldCodeHint')}</p>
          <input
            id="admin-new-schema-code"
            className={gov.editorInput}
            value={code}
            spellCheck={false}
            aria-invalid={code.length > 0 && !codeValid}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="admin-new-schema-title">
            {t('formSchemas.fieldTitle')}
          </label>
          <input
            id="admin-new-schema-title"
            className={gov.editorInput}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="admin-new-schema-effective">
            {t('formSchemas.fieldEffective')}
          </label>
          <input
            id="admin-new-schema-effective"
            className={gov.editorInputNarrow}
            type="date"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="admin-new-schema-base">
            {t('formSchemas.fieldBase')}
          </label>
          <p className={styles.fieldHint}>{t('formSchemas.fieldBaseHint')}</p>
          <Select
            id="admin-new-schema-base"
            value={baseSchemaId}
            onChange={setBaseSchemaId}
            options={[
              { value: '', label: t('formSchemas.noBase') },
              ...platformTemplates.map((candidate) => ({
                value: candidate.id,
                label: `${candidate.code} v${candidate.version}`,
                description: candidate.title,
              })),
            ]}
          />
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="modal-button" disabled={!canSubmit} onClick={submit}>
            {submitting ? t('governance.transition.working') : t('formSchemas.createConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}